import axios from 'axios';
import { handleApiError } from '../global/handleApiError';
import { fetchEmails } from '../apis/fetchDistributionListApi';
import { fetchSolutionDataByName } from '../apis/fetchBusinessSolutionApi';
import ReactDOMServer from 'react-dom/server';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const SENDER_EMAIL = process.env.REACT_APP_SENDER_EMAIL;

async function getDistributionListKey(distributionLists: string, csrfToken: string | null): Promise<unknown> {
  const solution = await fetchSolutionDataByName(distributionLists, csrfToken) as unknown;
  return (solution as { id: string }).id;
}

async function extractImagesAsAttachments(htmlString: string): Promise<{ html: string; attachments: unknown[] }> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const images = doc.querySelectorAll("img");

  const attachments: unknown[] = [];

  const imagePromises = Array.from(images).map(async (img, index) => {
    try {
      const response = await fetch(img.src);
      const blob = await response.blob();

      const reader = new FileReader();
      return new Promise<void>((resolve) => {
        reader.onloadend = () => {
          const base64Data = reader.result as string;
          const contentId = `image${index}`;
          img.setAttribute("src", `cid:${contentId}`); 
          attachments.push({
            filename: `image${index}.png`,
            content: base64Data.split(",")[1],
            encoding: "base64",
            cid: contentId,
          });
          resolve();
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error processing image for attachment:", error);
    }
  });

  await Promise.all(imagePromises);

  const updatedHtml = doc.documentElement.querySelector("body")?.innerHTML || doc.documentElement.outerHTML;

  return { html: updatedHtml, attachments };
}

// API function to send an email
export const sendEmail = async (
  includedServices: string,
  subject: string,
  announcementPreview: unknown,
  csrfToken: string | null,
  ifKey = false,
  replyTo?: string
): Promise<void> => {
  if (!csrfToken) throw new Error("CSRF token not available");

  // Convert distributionLists to the List of strings
  let distributionLists = includedServices.split(',').map(list => list.trim());

  // Remove duplicates from distributionLists
  distributionLists = Array.from(new Set(distributionLists));

  // Remove any undefined or empty strings
  distributionLists = distributionLists.filter(Boolean);

  let distributionListFormatted: string[] = [];

  if (ifKey) {
    // If keys are directly provided, use them as is
    distributionListFormatted = distributionLists;
  } else {
    // Convert distribution list names to keys
    for (let i = 0; i < distributionLists.length; i++) {
      const key = await getDistributionListKey(distributionLists[i], csrfToken);
      if (key !== undefined && typeof key === 'string') {
        distributionListFormatted.push(key);
      }
    }
  }

  distributionListFormatted = distributionListFormatted.filter(Boolean);

  let emailsToSendList: Array<unknown> = [];

  await Promise.all(distributionListFormatted.map(async (element) => {
    const emails = await fetchEmails(element);
    emailsToSendList.push(...emails); // Spread emails into the array
  }));

  // Remove duplicates from emailsToSendList
  emailsToSendList = Array.from(new Set(emailsToSendList));

  // Remove any undefined or empty strings
  emailsToSendList = emailsToSendList.filter(Boolean);

  const emailsToSend = emailsToSendList.join(',');

  const emailHtml = ReactDOMServer.renderToStaticMarkup(announcementPreview as React.ReactNode);

  const { html: emailHtmlWithAttachments, attachments } = await extractImagesAsAttachments(emailHtml);

  try {
    const xssParam = await getXssParam();
    const response = await axios.post(`${API_BASE_URL}/email/send`,
      {
        from: SENDER_EMAIL,
        to: emailsToSend,
        subject: subject,
        html: emailHtmlWithAttachments,
        attachments: attachments, 
        replyTo: replyTo, 
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Pass-XSS-Param': xssParam || '',
          'CSRF-Token': csrfToken, // Add CSRF token for security
        },
        withCredentials: true, // Include cookies (e.g., connect.sid)
      }
    );

    return response.data.message;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// API function to fetch emails from a distribution list
const getXssParam = async (): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/email/param`, {
      withCredentials: true, 
    });
    return response.data.PASS_XSS_PARAM;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};